import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SheetAgentHelpPage } from "@/constants/enums";
import { cn } from "@/lib/utils";

interface HelpStepperProps {
  /** Current active page index */
  currentPage: SheetAgentHelpPage;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (_page: SheetAgentHelpPage) => void;
  /** Array of page labels/names */
  pageLabels: string[];
}

/**
 * Vertical stepper component for navigating between help pages.
 * Displays step indicators with page names on the left side.
 *
 * @component
 */
function HelpStepper({ currentPage, totalPages, onPageChange, pageLabels }: HelpStepperProps) {
  const { t } = useTranslation("modals");
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < totalPages - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange((currentPage - 1) as SheetAgentHelpPage);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onPageChange((currentPage + 1) as SheetAgentHelpPage);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Vertical Step Indicators with Labels */}
      <div className="flex-1 space-y-1">
        {Array.from({ length: totalPages }).map((_, index) => {
          const step = index as SheetAgentHelpPage;
          const isActive = step === currentPage;
          const isCompleted = step < currentPage;
          const label = pageLabels[index] || `Step ${index + 1}`;

          return (
            <div key={index} className="flex items-start gap-3">
              {/* Vertical Line */}
              <div className="flex flex-col items-center pt-1">
                <div
                  className={cn(
                    "h-3 w-3 rounded-full border-2 transition-colors shrink-0",
                    isActive && "bg-blue-500 border-blue-500",
                    isCompleted && "bg-primary border-primary",
                    !isActive && !isCompleted && "bg-background"
                  )}
                />
                {index < totalPages - 1 && (
                  <div
                    className={cn(
                      "w-0.5 flex-1 min-h-8 transition-colors",
                      isCompleted ? "bg-primary/50" : "bg-muted"
                    )}
                  />
                )}
              </div>

              {/* Label Button */}
              <button
                onClick={() => onPageChange(step)}
                className={cn(
                  "text-left py-1 px-2 rounded-md transition-colors flex-1",
                  "hover:bg-accent hover:text-accent-foreground",
                  isActive && "bg-accent text-accent-foreground font-medium",
                  !isActive && "text-muted-foreground"
                )}
                aria-label={t("sheetagent.help.stepper.gotopage", { page: label })}
              >
                <div className="text-sm">{label}</div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          aria-label={t("sheetagent.help.stepper.previouspage")}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label={t("sheetagent.help.stepper.nextpage")}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default HelpStepper;
